import {
  ApolloClient,
  InMemoryCache,
  from,
  ApolloLink,
  HttpLink,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { organizationStore } from "../store/organization";
import { userStore } from "../store/user";

const HASURA_ENDPOINT = import.meta.env.VITE_SERVICE_HASURA_ENDPOINT;
if (!HASURA_ENDPOINT) throw new Error("missing VITE_SERVICE_HASURA_ENDPOINT");

let client: ApolloClient<any>;
let unauthedClient: ApolloClient<any>;

const link = new HttpLink({
  uri: HASURA_ENDPOINT,
});

const authMiddleware = new ApolloLink((operation, forward) => {
  // add the authorization to the headers
  const token = localStorage.getItem("ron-auth");
  operation.setContext({
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  return forward(operation);
});

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  const { getState } = userStore;
  const { getState: getOrganizationState } = organizationStore;

  if (graphQLErrors)
    if (
      graphQLErrors.some(
        ({ message }) =>
          message.includes("Authorization") || message.includes("JWT")
      ) &&
      window.location.pathname !== "/login"
    ) {
      const setUser = getState().setUser;
      const setOrganizationId = getOrganizationState().setOrganizationId;

      // If there is an authentication error redirect to login
      setUser(undefined);
      setOrganizationId(999999999);

      localStorage.removeItem("ron-auth");
      window.location.href = "/login";
    }

  const opName = operation?.operationName;
  if (opName !== "InsertAdminAnalyticsEvents") {
    // Dynamic import avoids a static cycle: ApolloClientFactory <-> analytics/tracker -> stores.
    // By the time onError fires, the analytics module is fully initialized.
    import("../modules/analytics")
      .then(({ trackEvent }) => {
        if (graphQLErrors) {
          graphQLErrors.forEach((err) => {
            trackEvent("error_shown", {
              error_code: (err.extensions?.code as string) ?? "graphql",
              payload: {
                message: err.message,
                operation: opName ?? null,
                source: "graphql",
              },
            });
          });
        }
        if (networkError) {
          trackEvent("error_shown", {
            error_code: "network",
            payload: {
              message: networkError.message ?? String(networkError),
              operation: opName ?? null,
              source: "network",
            },
          });
        }
      })
      .catch(() => {
        // analytics must never block UX
      });
  }

  if (networkError) console.log(`[Network error]: ${networkError}`);
});

export const createApolloClient = () => {
  if (client) return client;
  client = new ApolloClient({
    link: from([errorLink, authMiddleware, link]),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: { fetchPolicy: "network-only" },
      query: { fetchPolicy: "network-only" },
    },
  });
  return client;
};

export const createUnauthenticatedApolloClient = () => {
  if (unauthedClient) return unauthedClient;
  unauthedClient = new ApolloClient({
    link: from([errorLink, link]),
    cache: new InMemoryCache(),
  });
  return unauthedClient;
};
